/**
 * Seeded reviews source (Prompt G, Stage 5; profiled in seed rebuild Commit C).
 *
 * ASSUMPTION: seeded until a real reviews source exists. There is no in-app
 * workflow that captures Google/Facebook reviews, so this stands in for one with
 * a deterministic per-studio new-review count. Swap this module for the live
 * feed when the client wires one up; the `reviewsDelta` selector reads only
 * `reviewsInRange` + `REVIEWS_WEEKLY_TARGET`, so nothing downstream changes.
 *
 * Commit C replaced the old uniform PRNG (which made every studio noise around
 * the target) with a per-studio HEALTH PROFILE so the Reviews tile tells the
 * store-health story: the healthy hero (Coburg) sits clearly above the weekly
 * target, the troubled store (Frankston) clearly below, and everyone else lands
 * around target with minor variance. Deterministic - a fixed per-studio,
 * per-weekday table, no `Math.random`.
 */

/** ASSUMPTION: per-studio weekly review target; the client sets the real number. */
export const REVIEWS_WEEKLY_TARGET = 6;

/**
 * New reviews a studio lands on each weekday, indexed 0=Sun .. 6=Sat. Summed
 * across a day window by `reviewsInRange`. Tuned so a whole target week (six
 * days, Thu-Tue) lands near each studio's health profile:
 *  - Coburg (healthy hero): ~9/week, well above target.
 *  - Frankston (troubled): ~2/week, well below target.
 *  - everyone else: ~5-7/week, around target with minor variance.
 */
const REVIEW_WEEKDAY_PROFILE: Record<string, readonly number[]> = {
  //           Sun Mon Tue Wed Thu Fri Sat
  coburg: [1, 2, 1, 2, 2, 2, 1], // Thu-Tue = 9 (above target)
  frankston: [0, 1, 0, 0, 1, 0, 0], // Thu-Tue = 2 (below target)
  collingwood: [1, 1, 1, 1, 1, 1, 1], // 6
  windsor: [1, 1, 1, 1, 1, 2, 1], // Thu-Tue = 7 (healthy)
  'central-coast': [1, 1, 0, 1, 1, 1, 1], // Thu-Tue = 5
  'emu-plains': [1, 1, 1, 1, 1, 1, 1], // 6
  liverpool: [1, 1, 1, 1, 1, 1, 0], // Thu-Tue = 5
  brisbane: [1, 1, 1, 1, 1, 1, 1], // 6
  'gold-coast': [1, 0, 1, 1, 1, 1, 1], // Thu-Tue = 5
  mackay: [1, 1, 1, 1, 0, 1, 1], // Thu-Tue = 5
  tasmania: [1, 1, 1, 1, 1, 1, 1], // 6
};

/** Default profile for any studio not in the table (steady, around target). */
const DEFAULT_PROFILE: readonly number[] = [1, 1, 1, 1, 1, 1, 0];

/** Weekday (0=Sun..6=Sat) of a 'YYYY-MM-DD' day-key, read at noon UTC so it never drifts. */
function weekdayOfDayKey(dayKey: string): number {
  return new Date(`${dayKey}T12:00:00Z`).getUTCDay();
}

/** Deterministic new-review count for a studio on a given 'YYYY-MM-DD' day-key. */
export function reviewsOnDay(studioId: string, dayKey: string): number {
  const profile = REVIEW_WEEKDAY_PROFILE[studioId] ?? DEFAULT_PROFILE;
  return profile[weekdayOfDayKey(dayKey)] ?? 0;
}

/** New reviews for a studio across a set of day-keys. */
export function reviewsInRange(studioId: string, days: readonly string[]): number {
  return days.reduce((sum, d) => sum + reviewsOnDay(studioId, d), 0);
}
